import React, { createContext, useContext, useState, useEffect } from 'react';
import { Subscription, CourseFamily, Course, Instructor, CourseMode } from '@/types';

interface ClassSession {
  mode: CourseMode;
  start_date: string;
  end_date: string;
  duration_minutes: number;
  instructor_id: string;
  location_name?: string;
  location_address?: string;
}

// Interface para Subscription com relações
interface SubscriptionWithRelations extends Subscription {
  subscription_customers: {
    id: string;
    customer_role: 'owner' | 'participant' | 'billing_contact';
    customer: {
      id: string;
      name: string;
    };
  }[];
  parent_subscription?: {
    id: string;
    display_id: string;
  };
}

interface ClassWizardState {
  selectedCustomer: any | null;
  selectedSubscription: SubscriptionWithRelations | null;
  selectedCourseFamily: CourseFamily | null;
  selectedCourse: Course | null;
  sessions: ClassSession[];
  selectedSponsors: string[];
}

interface ClassWizardContextType {
  state: ClassWizardState;
  setState: React.Dispatch<React.SetStateAction<ClassWizardState>>;
  resetWizard: () => void;
}

const STORAGE_KEY = 'class-wizard-state';

const getInitialState = (): ClassWizardState => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      console.log('Restored wizard state from localStorage (raw):', parsed);
      
      // Check if it has old structure, if so, clear it
      if (parsed.currentStep !== undefined || parsed.stepsCompleted !== undefined) {
        console.log('Detected old wizard state structure, clearing localStorage...');
        localStorage.removeItem(STORAGE_KEY);
        return {
          selectedCustomer: null,
          selectedSubscription: null,
          selectedCourseFamily: null,
          selectedCourse: null,
          sessions: [],
          selectedSponsors: [],
        };
      }
      
      // Filter out old properties that we no longer use
      const cleanState = {
        selectedCustomer: parsed.selectedCustomer || null,
        selectedSubscription: parsed.selectedSubscription || null,
        selectedCourseFamily: parsed.selectedCourseFamily || null,
        selectedCourse: parsed.selectedCourse || null,
        sessions: parsed.sessions || [],
        selectedSponsors: parsed.selectedSponsors || [],
      };
      
      console.log('Restored clean wizard state:', cleanState);
      return cleanState;
    }
  } catch (error) {
    console.error('Error loading saved wizard state:', error);
    localStorage.removeItem(STORAGE_KEY);
  }
  
  return {
    selectedCustomer: null,
    selectedSubscription: null,
    selectedCourseFamily: null,
    selectedCourse: null,
    sessions: [],
    selectedSponsors: [],
  };
};

const ClassWizardContext = createContext<ClassWizardContextType | undefined>(undefined);

export const ClassWizardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ClassWizardState>(getInitialState());
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Mark as initialized after first render
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      console.log('Saved wizard state to localStorage:', state);
    }
  }, [state, isInitialized]);

  const resetWizard = () => {
    localStorage.removeItem(STORAGE_KEY);
    setState({
      selectedCustomer: null,
      selectedSubscription: null,
      selectedCourseFamily: null,
      selectedCourse: null,
      sessions: [],
      selectedSponsors: [],
    });
  };

  return (
    <ClassWizardContext.Provider value={{ state, setState, resetWizard }}>
      {children}
    </ClassWizardContext.Provider>
  );
};

export const useClassWizard = () => {
  const context = useContext(ClassWizardContext);
  if (context === undefined) {
    console.error('useClassWizard called outside of ClassWizardProvider. Current location:', window.location.pathname);
    throw new Error('useClassWizard must be used within a ClassWizardProvider');
  }
  return context;
}; 